import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../services/api';
import './ProjectImportPage.css';

const formatSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const extractApiError = (e) => {
  const body = e?.response?.data;
  if (typeof body === 'string' && body) return body;
  if (body?.title) return body.title;
  if (body?.message) return body.message;
  if (body?.detail) return body.detail;
  return e?.message || 'Import failed.';
};

const ProjectImportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [success, setSuccess] = useState(null);

  const resetAll = () => {
    setFile(null);
    setParsed(null);
    setError(null);
    setSuccess(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const showError = (title, message) => {
    setParsed(null);
    setError({ title, message });
  };

  const handleFile = (f) => {
    if (!f) return;
    setError(null);
    setSuccess(null);
    setParsed(null);

    if (!f.name.toLowerCase().endsWith('.json')) {
      setFile(null);
      showError('Invalid File Type', 'Only .json files are accepted. Please select a valid project plan JSON file.');
      return;
    }

    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      let data;
      try {
        data = JSON.parse(e.target.result);
      } catch (err) {
        showError('Invalid JSON', `The file could not be parsed as valid JSON: ${err.message}`);
        return;
      }
      if (!data.name || !data.code) {
        showError('Missing Required Fields', 'The JSON must contain "name" and "code" fields.');
        return;
      }
      setParsed(data);
    };
    reader.onerror = () => showError('Read Error', 'Could not read the selected file.');
    reader.readAsText(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFile(e.dataTransfer.files[0]);
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const doImport = async () => {
    if (!file || !parsed) return;
    setImporting(true);
    setError(null);
    try {
      const result = await projectService.import(file);
      setSuccess({
        id: result?.id ?? result?.Id,
        name: result?.name ?? parsed.name,
        code: result?.code ?? parsed.code,
        roleCount: (parsed.roles || []).length,
        taskCount: (parsed.tasks || []).length,
      });
      setParsed(null);
      setFile(null);
    } catch (e) {
      const msg = extractApiError(e);
      if (msg.toLowerCase().includes('already exists')) {
        showError('Duplicate Project Code', msg);
      } else {
        showError('Import Failed', msg);
      }
    } finally {
      setImporting(false);
    }
  };

  const roles = parsed?.roles || [];
  const tasks = parsed?.tasks || [];

  return (
    <div className="pip-root">
      <div className="pip-topbar">
        <button className="pip-back" onClick={() => navigate('/projects')}>&larr; Back to Projects</button>
        <h1>Import Project</h1>
      </div>
      <div className="pip-content">
        <div className="pip-card">
          {!success && (
            <>
              <div
                className={'pip-drop' + (dragOver ? ' pip-dragover' : '') + (file ? ' pip-has-file' : '')}
                onClick={() => fileInputRef.current?.click()}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="pip-file-input"
                  accept=".json,application/json"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <div className="pip-icon">&#x1F4C4;</div>
                <h3>Drop your project plan JSON file here</h3>
                <p>Supports the HRApp project plan format with roles, tasks, and allocations</p>
                <div className="pip-or">&mdash; or &mdash;</div>
                <span className="pip-browse">Browse files</span>
              </div>

              {file && (
                <div className="pip-file-info">
                  <span className="pip-file-name">{file.name}</span>
                  <span className="pip-file-size">({formatSize(file.size)})</span>
                  <button className="pip-remove" onClick={(e) => { e.stopPropagation(); resetAll(); }}>&#x2715; Remove</button>
                </div>
              )}

              {error && (
                <div className="pip-error">
                  <div className="pip-error-title">{error.title}</div>
                  <div>{error.message}</div>
                </div>
              )}

              {parsed && (
                <>
                  <div className="pip-preview">
                    <div className="pip-preview-header"><h3>&#x1F4CB; Import Preview</h3></div>
                    <div className="pip-preview-body">
                      <div className="pip-preview-grid">
                        <div className="pip-preview-item">
                          <div className="pip-label">Project Name</div>
                          <div className="pip-value pip-big">{parsed.name}</div>
                        </div>
                        <div className="pip-preview-item">
                          <div className="pip-label">Project Code</div>
                          <div className="pip-value pip-big">{parsed.code}</div>
                        </div>
                        <div className="pip-preview-item">
                          <div className="pip-label">Client</div>
                          <div className="pip-value">{parsed.clientName || '\u2014'}</div>
                        </div>
                        <div className="pip-preview-item">
                          <div className="pip-label">Currency</div>
                          <div className="pip-value">{parsed.currencyCode || 'EUR'}</div>
                        </div>
                      </div>
                      <div className="pip-preview-grid">
                        <div className="pip-preview-item">
                          <div className="pip-label">Roles</div>
                          <div className="pip-value">{roles.length}</div>
                        </div>
                        <div className="pip-preview-item">
                          <div className="pip-label">Tasks</div>
                          <div className="pip-value">{tasks.length}</div>
                        </div>
                      </div>
                      <div className="pip-preview-section">
                        <h4>Roles</h4>
                        <div className="pip-role-chips">
                          {roles.map((r, i) => (
                            <span key={i} className="pip-role-chip">
                              {(r.abbreviation || (r.roleName || '').substring(0, 2).toUpperCase())} &mdash; {r.roleName} <span className="pip-rate">(&euro;{r.sellRatePerHour}/h)</span>
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="pip-preview-section">
                        <h4>Task Structure</h4>
                        <div className="pip-task-tree">
                          {tasks.map((t, i) => {
                            const indent = (t.outlineLevel || 0) * 20;
                            const isSum = !!t.isSummary;
                            const icon = isSum ? '\u25BC' : '\u2022';
                            const dur = t.durationDays ? `${t.durationDays}d` : (t.durationHours ? `${t.durationHours}h` : '');
                            const mode = t.taskMode || 'AUTO';
                            const pred = t.predecessor ? ` pred:${t.predecessor}` : '';
                            const aCount = (t.roleAssignments || []).length;
                            const meta = [dur, mode, aCount ? `${aCount} roles` : '', pred].filter(Boolean).join(' \u00B7 ');
                            return (
                              <div key={i} style={{ paddingLeft: indent }} className={isSum ? 'pip-summary' : 'pip-leaf'}>
                                {icon} {t.name}<span className="pip-meta">{meta}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pip-actions">
                    <button className="pip-btn pip-btn-secondary" onClick={resetAll} disabled={importing}>Cancel</button>
                    <button className="pip-btn pip-btn-primary" onClick={doImport} disabled={importing || !parsed}>
                      {importing ? 'Importing...' : 'Import Project'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {success && (
            <div className="pip-success">
              <div className="pip-check">&#x2705;</div>
              <h3>Import Successful!</h3>
              <p>{success.name} ({success.code}) &mdash; {success.roleCount} roles, {success.taskCount} tasks imported.</p>
              {success.id && (
                <button className="pip-link" onClick={() => navigate(`/projects/${success.id}`)}>
                  Open project detail &rarr;
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectImportPage;
